import { COMPONENT_TYPE, NoData } from 'modules/experiments/components/ui';
import { analyticsReloadPerformanceTabData } from 'modules/experiments/ducks/actions';
import {
  getCampaignMessageMedium,
  getExperimentLoadingState,
  getExperimentMetricsOptionAndDefault,
  getExperimentPerformanceTabErrorState,
  getExperimentPerformanceTabLoadingState,
  getExperimentVariantBreakdown,
} from 'modules/experiments/ducks/selectors';
import { useExperimentSearchParams, useExperimentsTranslations } from 'modules/experiments/hooks';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExperimentNotFound } from 'ui/illustrations/ExperimentNotFound';
import { InfoSection, TimeRangeHeader, TimeRangeSection } from './Styled';
import { ExperimentDateTimePicker, ExperimentInfo } from './dateTimePicker';
import { VariantBreakdown } from './variantBreakdown';
import { VariantPerformance } from './variantPerformance';
import { EmptyTab, TabSpinner } from './variantPerformance/Styled';

export const ExperimentPerformanceTab = () => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const isPerformanceTabLoading = useSelector(getExperimentPerformanceTabLoadingState);
  const hasPerformanceTabErrored = useSelector(getExperimentPerformanceTabErrorState);
  const isPageLoading = useSelector(getExperimentLoadingState);
  const variantList = useSelector(getExperimentVariantBreakdown);
  const { options } = useSelector(getExperimentMetricsOptionAndDefault);
  const messageMedium = useSelector(getCampaignMessageMedium);
  const metricMap = useMemo(
    () => options.reduce((acc, { value, data }) => ({ ...acc, [value]: data.identifier }), {}),
    [options],
  );
  const { experimentId, startDateTime, endDateTime } = useExperimentSearchParams();

  const renderTabCards = () => {
    switch (true) {
      case isPageLoading:
        return <EmptyTab />;

      case isPerformanceTabLoading || isPageLoading:
        return <TabSpinner />;

      case hasPerformanceTabErrored:
        return (
          <NoData
            testId="experiment-performance-tab-snapped"
            componentType={COMPONENT_TYPE.TAB}
            Icon={ExperimentNotFound}
            title={t('noData.card.title')}
            subTitle={t('noData.card.subTitle')}
            linkText={t('noData.card.link')}
            onClick={() =>
              dispatch(
                analyticsReloadPerformanceTabData({
                  id: experimentId,
                  messageMedium,
                  startTime: startDateTime,
                  endTime: endDateTime,
                }),
              )
            }
          />
        );

      default:
        return (
          <>
            <VariantBreakdown options={options} metricMap={metricMap} variantList={variantList} />
            <VariantPerformance options={options} metricMap={metricMap} variantList={variantList} />
          </>
        );
    }
  };

  return (
    <>
      <TimeRangeHeader data-test="experiments-monitor-time-range-header">
        <TimeRangeSection data-test="experiments-monitor-time-range-picker-section">
          <ExperimentDateTimePicker />
        </TimeRangeSection>
        <InfoSection data-test="experiments-monitor-time-range-info-section">
          <ExperimentInfo />
        </InfoSection>
      </TimeRangeHeader>
      {renderTabCards()}
    </>
  );
};
